import { Recommendation } from './multi-recommendation';
import { ProductRecommendationResponse } from './single-recommendation';
import { z } from 'zod';

export type NGCFlow = 'v1' | 'v2' | 'v3' | 'v4';

export enum ActionTypes {
  SET_RECOMMENDATION = 'recommendation/set',
  SET_RECOMMENDATIONS = 'recommendations/set',
  SET_SELECTED_RECOMMENDATION = 'recommendations/setSelected',
  SET_VIEW_DETAILS_RECOMMENDATION = 'recommendations/setViewDetails',
  SET_RECOMMENDATION_FLOW = 'recommendation/setFlow',
  SET_NGC_CONFIGURATION = 'recommendation/setNGCConfiguration',
  SET_VAS_RECOMMENDATIONS = 'recommendations/setVASRecommendations',
}

export interface SetRecommendationFlowAction {
  type: ActionTypes.SET_RECOMMENDATION_FLOW;
  recommendationFlow: NGCFlow;
}

export interface SetRecommendationsAction {
  type: ActionTypes.SET_RECOMMENDATIONS;
  recommendations: Recommendation[];
}

export interface SetVASRecommendationsAction {
  type: ActionTypes.SET_VAS_RECOMMENDATIONS;
  vasRecommendations: string[];
}

export interface SetSelectedRecommendationAction {
  type: ActionTypes.SET_SELECTED_RECOMMENDATION;
  recommendationId: string;
}

export interface SetViewDetailsRecommendationAction {
  type: ActionTypes.SET_VIEW_DETAILS_RECOMMENDATION;
  recommendationId: string;
}

export interface SetRecommendationAction {
  type: ActionTypes.SET_RECOMMENDATION;
  productRecommendation: ProductRecommendationResponse;
}

export interface SetNGCConfigurationAction {
  type: ActionTypes.SET_NGC_CONFIGURATION;
  ngcConfiguration: NGCConfiguration;
}

export type Actions =
  | SetRecommendationAction
  | SetRecommendationsAction
  | SetVASRecommendationsAction
  | SetSelectedRecommendationAction
  | SetRecommendationFlowAction
  | SetViewDetailsRecommendationAction
  | SetNGCConfigurationAction;

const NGCConfigurationDataSchema = z.object({
  ngcFlow: z.enum(['v1', 'v2', 'v3', 'v4']),
  model: z.enum(['ml', 'revenue_ml']),
  showFaq: z.boolean().optional().default(false),
  showMoreDetailsByop: z.boolean().optional().default(true),
  altProductExplainerVersion: z.enum(['v2', 'v3']).optional().default('v2'),
  v2AddOnCheckbox: z.boolean().optional().default(false),
  claimRewardsLocation: z.enum(['default', 'header']).optional().default('default'),
  enableStickyCtaOnQuiz: z.boolean().optional().default(false),
});

const NGCConfigurationComponentSchema = z.object({
  variantId: z.string(),
  variantName: z.string(),
  variantCustomId: z.string(),
  componentId: z.string(),
  componentName: z.string(),
  componentCustomId: z.string(),
  data: NGCConfigurationDataSchema,
});

export const NGCConfigurationMonarchResponseNestedSchema = z
  .object({
    ngcConfiguration: NGCConfigurationComponentSchema,
  })
  .transform((data) => ({
    recommendationFlow: data.ngcConfiguration.data.ngcFlow,
    model: data.ngcConfiguration.data.model,
    showFaq: data.ngcConfiguration.data.showFaq,
    showMoreDetailsByop: data.ngcConfiguration.data.showMoreDetailsByop ?? true,
    altProductExplainerVersion: data.ngcConfiguration.data.altProductExplainerVersion ?? 'v2',
    v2AddOnCheckbox: data.ngcConfiguration.data.v2AddOnCheckbox ?? false,
    claimRewardsLocation: data.ngcConfiguration.data.claimRewardsLocation ?? 'default',
    enableStickyCtaOnQuiz: data.ngcConfiguration.data.enableStickyCtaOnQuiz ?? false,
  }));

export const NGCConfigurationMonarchResponseSchema = NGCConfigurationComponentSchema.transform((data) => ({
  recommendationFlow: data.data.ngcFlow,
  model: data.data.model,
  showFaq: data.data.showFaq,
  showMoreDetailsByop: data.data.showMoreDetailsByop,
  altProductExplainerVersion: data.data.altProductExplainerVersion,
  v2AddOnCheckbox: data.data.v2AddOnCheckbox,
  claimRewardsLocation: data.data.claimRewardsLocation,
  enableStickyCtaOnQuiz: data.data.enableStickyCtaOnQuiz,
}));

export type NGCConfiguration = z.infer<typeof NGCConfigurationMonarchResponseSchema>;
